import { useState } from 'react';
import { Product } from '@/mocks/products';
import { useCart } from '@/hooks/useCart';

interface ProductCardProps {
  product: Product;
}

const tierConfig: Record<string, { label: string; color: string; bg: string }> = {
  single: { label: '單品', color: 'text-[#8B4513]', bg: 'bg-[#8B4513]/10' },
  set: { label: '套餐', color: 'text-[#C0392B]', bg: 'bg-[#C0392B]/10' },
  premium: { label: '高端', color: 'text-[#D4A96A]', bg: 'bg-[#1a1a1a]' },
};

const badgeColors: Record<string, string> = {
  熱銷: 'bg-red-500',
  推薦: 'bg-[#8B4513]',
  頂級: 'bg-amber-600',
};

export default function ProductCard({ product }: ProductCardProps) {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addToCart(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const tier = tierConfig[product.tier];
  const isPremium = product.tier === 'premium';

  return (
    <div
      className={`rounded-xl overflow-hidden group cursor-pointer border transition-all duration-300 ${
        isPremium
          ? 'bg-[#1a1a1a] border-[#D4A96A]/30 hover:border-[#D4A96A]/70'
          : 'bg-white border-gray-100 hover:border-[#D4A96A]/40'
      }`}
    >
      {/* Image */}
      <div
        className={`relative h-52 overflow-hidden ${
          isPremium ? 'bg-[#111]' : 'bg-[#FAF8F5]'
        }`}
      >
        {product.badge && (
          <span
            className={`absolute top-3 left-3 z-10 text-white text-xs font-bold px-2.5 py-1 rounded-full ${
              badgeColors[product.badge] || 'bg-gray-600'
            }`}
          >
            {product.badge}
          </span>
        )}
        {/* Tier badge */}
        <span
          className={`absolute top-3 right-3 z-10 text-xs font-bold px-2.5 py-1 rounded-full ${tier.bg} ${tier.color}`}
        >
          {tier.label}
        </span>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="mb-2">
          <h4
            className={`text-sm font-bold leading-tight ${
              isPremium ? 'text-white' : 'text-[#1a1a1a]'
            }`}
          >
            {product.name}
          </h4>
          <p className={`text-xs mt-0.5 ${isPremium ? 'text-[#D4A96A]/60' : 'text-gray-400'}`}>
            {product.nameEn}
          </p>
        </div>

        <p
          className={`text-xs leading-relaxed mt-2 mb-3 line-clamp-2 ${
            isPremium ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {product.description}
        </p>

        {/* Contents & Servings */}
        <div
          className={`rounded-lg p-2.5 mb-3 ${
            isPremium ? 'bg-white/5' : 'bg-[#FAF8F5]'
          }`}
        >
          <div className="flex items-start gap-1.5 mb-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 flex-shrink-0">
              <i
                className={`ri-list-check text-xs ${
                  isPremium ? 'text-[#D4A96A]' : 'text-[#8B4513]'
                }`}
              ></i>
            </span>
            <p
              className={`text-xs leading-relaxed ${
                isPremium ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              {product.contents}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
              <i
                className={`ri-group-line text-xs ${
                  isPremium ? 'text-[#D4A96A]' : 'text-[#8B4513]'
                }`}
              ></i>
            </span>
            <p
              className={`text-xs ${isPremium ? 'text-gray-400' : 'text-gray-500'}`}
            >
              適合 {product.servings}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <span
              className={`text-lg font-black ${
                isPremium ? 'text-[#D4A96A]' : 'text-[#1a1a1a]'
              }`}
            >
              NT$ {product.price.toLocaleString()}
            </span>
            <span
              className={`text-xs ml-1 ${isPremium ? 'text-gray-500' : 'text-gray-400'}`}
            >
              / {product.unit}
            </span>
          </div>
          <button
            onClick={handleAdd}
            className={`flex items-center space-x-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              added
                ? 'bg-green-500 text-white'
                : isPremium
                ? 'bg-[#D4A96A] text-[#1a1a1a] hover:bg-[#C49A5A]'
                : 'bg-[#8B4513] text-white hover:bg-[#6B3410]'
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i
                className={`text-sm ${added ? 'ri-check-line' : 'ri-shopping-cart-2-line'}`}
              ></i>
            </span>
            <span>{added ? '已加入' : '加入購物車'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
